import { WEB_COLORS } from "@/lib/constants";
import { LocationOn } from "@mui/icons-material";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { debounce } from "@mui/material/utils";
import * as React from "react";

function loadScript(src, position, id) {
  if (!position) {
    return;
  }

  const script = document.createElement("script");
  script.setAttribute("async", "");
  script.setAttribute("id", id);
  script.src = src;
  position.appendChild(script);
}

const autocompleteService = { current: null };

 const MapsAutocomplete = React.forwardRef(({
  handleOnBlur,
  handleReset,
  handleOnSelect,
  keyName,
  label,
  sx,
},ref) =>{
  const [value, setValue] = React.useState(null);
  const [inputValue, setInputValue] = React.useState("");
  const [options, setOptions] = React.useState([]);
  const loaded = React.useRef(false);

  if (typeof window !== "undefined" && !loaded.current) {
    if (!document.querySelector("#google-maps")) {
      loadScript(
        `https://maps.googleapis.com/maps/api/js?key=${process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY}&language=en&region=us&libraries=places`,
        document.querySelector("head"),
        "google-maps"
      );
    }

    loaded.current = true;
  }

  React.useImperativeHandle(ref, () => ({
    reset: () => {
      setValue(null);
      setInputValue("");
    }
  }));

  const fetch = React.useMemo(
    () =>
      debounce((request, callback) => {
        autocompleteService.current.getPlacePredictions(request, callback);
      }, 400),
    []
  );

  React.useEffect(() => {
    let active = true;

    // Wait for Google Maps script to be loaded
    if (
      typeof window === "undefined" ||
      !window.google ||
      !window.google.maps ||
      !window.google.maps.places
    ) {
      return undefined;
    }
    if (!autocompleteService.current) {
      autocompleteService.current =
        new window.google.maps.places.AutocompleteService();
    }
    if (!autocompleteService.current) {
      return undefined;
    }

    if (inputValue === "") {
      setOptions(value ? [value] : []);
      return undefined;
    }
    fetch(
      {
        input: inputValue,
        componentRestrictions: { country: "in" },
      },
      (results) => {
        if (active) {
          let newOptions = [];

          if (value) {
            newOptions = [value];
          }

          if (results) {
            newOptions = [...newOptions, ...results];
          }

          setOptions(newOptions);
        }
      }
    );

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  const handleOptionChange = (event, newValue) => {
    if (newValue) {
      const placeId = newValue.place_id;
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ placeId: placeId }, (results, status) => {
        if (status === "OK" && results[0]) {
          const location = results[0]?.geometry?.location; // Get the location object
          const latitude = location?.lat(); // Get latitude
          const longitude = location?.lng(); // Get longitude
          handleOnSelect({
            lat: latitude,
            long: longitude,
            googleApiAddData: results,
          });
        }
      });
    }
    setValue(newValue); // Always update the value to trigger the API call
  };

  const handleBlur = () => {
    handleOnBlur && handleOnBlur(keyName, true);
  };

  React.useEffect(() => {
    if (!value) {
      handleReset && handleReset();
    }
  }, [value]);
  return (
    <Autocomplete
      id="google-map-demo"
      getOptionLabel={(option) =>
        typeof option === "string" ? option : option.description
      }
      filterOptions={(x) => x}
      required
      autoComplete
      sx={{
        "& .MuiOutlinedInput-root": {
          borderRadius: "8px",
        },
        ...sx
      }}
      value={value}
      options={options}
      includeInputInList
      filterSelectedOptions
      noOptionsText="No locations"
      onChange={handleOptionChange}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      onBlur={handleBlur}
      renderInput={(params) => (
        <TextField required {...params} label={label} fullWidth />
      )}
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const matches =
          option.structured_formatting.main_text_matched_substrings || [];
        const parts =
          (option.structured_formatting.main_text,
          matches.map((match, i) => [
            match.offset,
            match.offset + match.length,
          ]));
        return (
          <li key={key} {...optionProps}>
            <Grid container sx={{ alignItems: "center" }}>
              <Grid item sx={{ display: "flex", width: 44 }}>
                <LocationOn sx={{ color: WEB_COLORS.primary }} />
              </Grid>
              <Grid
                item
                sx={{ width: "calc(100% - 44px)", wordWrap: "break-word" }}
              >
                {parts.map((part, index) => (
                  <Box
                    key={index}
                    component="span"
                    sx={{ fontWeight: part.highlight ? "bold" : "regular" }}
                  >
                    {part.text}
                  </Box>
                ))}
                <Typography variant="body2" color="text.secondary">
                  {option.structured_formatting.main_text}{" "}
                  {option.structured_formatting.secondary_text}
                </Typography>
              </Grid>
            </Grid>
          </li>
        );
      }}
    />
  );
});


export default MapsAutocomplete;
